import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LEAVE_TYPES,
  calculateAccruedAnnualLeave,
  calculateBusinessDays,
  cancelLeaveRequest,
  createLeaveRequest,
  getLeaveRequests,
  reviewLeaveRequest
} from "../services/leaveService";
import { createAdminNotification } from "../services/notificationService";

const currentYear = new Date().getFullYear();

const statusLabels = {
  pending: "Onay Bekliyor",
  approved: "Onaylandı",
  rejected: "Reddedildi",
  cancelled: "İptal Edildi"
};

const statusStyles = {
  pending: { background: "#fef3c7", color: "#92400e" },
  approved: { background: "#dcfce7", color: "#166534" },
  rejected: { background: "#fee2e2", color: "#991b1b" },
  cancelled: { background: "#e2e8f0", color: "#475569" }
};

function LeaveManagementView({ user, users = [], isAdmin = false, themeMode = "light" }) {
  const userId = user?.uid || user?.id || "";
  const employeeName = user?.name || user?.displayName || user?.email || "";
  const employeeEmail = user?.email || "";
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminTab, setAdminTab] = useState("requests");
  const [yearFilter, setYearFilter] = useState(currentYear);
  const [form, setForm] = useState({
    leaveType: "annual",
    startDate: "",
    endDate: "",
    description: "",
    attachmentUrl: ""
  });
  const [reviewNotes, setReviewNotes] = useState({});
  const isDark = themeMode === "dark";
  const colors = {
    text: isDark ? "#e5edf6" : "#0f172a",
    heading: isDark ? "#e5edf6" : "#124254",
    muted: isDark ? "#94a3b8" : "#64748b",
    card: isDark ? "#162235" : "#fff",
    soft: isDark ? "#1e293b" : "#f8fafc",
    input: isDark ? "#0f172a" : "#fff",
    border: isDark ? "#334155" : "#cbd5e1",
    cardBorder: isDark ? "#27364d" : "#e2e8f0",
    primary: isDark ? "#155e75" : "#124254",
    warningBg: isDark ? "#2a1d12" : "#fff7ed",
    warningBorder: isDark ? "#92400e" : "#fed7aa",
    warningText: isDark ? "#fbbf24" : "#9a3412",
    successText: isDark ? "#86efac" : "#166534"
  };
  const statusBadgeStyles = isDark
    ? {
        pending: { background: "#3a2a10", color: "#fbbf24" },
        approved: { background: "#123423", color: "#86efac" },
        rejected: { background: "#3a1414", color: "#fca5a5" },
        cancelled: { background: "#263244", color: "#cbd5e1" }
      }
    : statusStyles;

  const loadData = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const requestData = await getLeaveRequests({ userId, isAdmin });
      setRequests(requestData);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const requestedDays = calculateBusinessDays(form.startDate, form.endDate);

  const getBalanceSummary = (employee, year) => {
    const employeeId = employee?.uid || employee?.id || "";
    const now = new Date();
    const asOfDate = Number(year) === now.getFullYear()
      ? now
      : new Date(Number(year), 11, 31, 12);
    const entitlement = calculateAccruedAnnualLeave(employee?.hireDate, asOfDate);
    const used = requests
      .filter(
        (item) =>
          item.employeeId === employeeId &&
          item.leaveType === "annual" &&
          item.status === "approved" &&
          String(item.startDate || "") <= asOfDate.toISOString().slice(0, 10)
      )
      .reduce((sum, item) => {
        const asOfKey = asOfDate.toISOString().slice(0, 10);
        const effectiveEnd = String(item.endDate || "") > asOfKey
          ? asOfKey
          : item.endDate;
        return sum + calculateBusinessDays(item.startDate, effectiveEnd);
      }, 0);

    return {
      entitlement,
      used,
      remaining: entitlement - used
    };
  };

  const ownSummary = getBalanceSummary(user, yearFilter);
  const adminEmails = users
    .filter((item) => item?.role === "admin" || item?.isAdmin)
    .map((item) => item.email)
    .filter(Boolean);

  const visibleRequests = useMemo(
    () => requests.filter(
      (item) => Number(String(item.startDate || "").slice(0, 4)) === Number(yearFilter)
    ),
    [requests, yearFilter]
  );
  const displayedRequests = isAdmin && adminTab === "mine"
    ? visibleRequests.filter((item) => item.employeeId === userId)
    : visibleRequests;

  const submitRequest = async () => {
    if (!form.startDate || !form.endDate || !requestedDays) {
      alert("Geçerli başlangıç ve bitiş tarihleri seçmelisiniz.");
      return;
    }

    if (form.leaveType === "annual") {
      if (!user?.hireDate) {
        alert("Yıllık izin talebi için işe giriş tarihiniz tanımlanmalıdır.");
        return;
      }

      if (requestedDays > ownSummary.remaining) {
        alert(`Yetersiz yıllık izin bakiyesi. Kalan hakkınız: ${ownSummary.remaining} gün.`);
        return;
      }
    }

    await createLeaveRequest({
      employeeId: userId,
      employeeName,
      employeeEmail,
      ...form
    });

    const leaveLabel = LEAVE_TYPES.find((type) => type.value === form.leaveType)?.label || form.leaveType;

    await createAdminNotification({
      title: "Yeni izin talebi",
      body: `${employeeName} ${leaveLabel} talebi oluşturdu. Tarih: ${form.startDate} - ${form.endDate}`,
      type: "leave-request",
      link: "leaveManagement",
      createdBy: employeeEmail
    });

    if (adminEmails.length) {
      const subject = `Yeni izin talebi - ${employeeName}`;
      const body = `Merhaba,\n\n${employeeName} tarafından yeni izin talebi oluşturuldu.\n\nİzin türü: ${leaveLabel}\nTarih: ${form.startDate} - ${form.endDate}\nGün: ${requestedDays} iş günü\nAçıklama: ${form.description || "-"}\n\nPanel üzerinden inceleyebilirsiniz.`;
      window.location.href = `mailto:${encodeURIComponent(adminEmails[0])}?cc=${encodeURIComponent(adminEmails.slice(1).join(","))}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
    setForm({
      leaveType: "annual",
      startDate: "",
      endDate: "",
      description: "",
      attachmentUrl: ""
    });
    await loadData();
    alert("İzin talebiniz onaya gönderildi.");
  };

  const reviewRequest = async (requestItem, status) => {
    const action = status === "approved" ? "onaylamak" : "reddetmek";
    if (!(await window.appConfirm(`${requestItem.employeeName} izin talebini ${action} istiyor musunuz?`, { title: "İzin talebi", type: status === "approved" ? "success" : "danger", confirmText: status === "approved" ? "Onayla" : "Reddet" }))) return;

    await reviewLeaveRequest({
      requestId: requestItem.id,
      status,
      reviewNote: reviewNotes[requestItem.id] || "",
      reviewerEmail: employeeEmail
    });
    await loadData();
  };

  const cardStyle = {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 18,
    padding: 22,
    boxShadow: isDark ? "0 8px 24px rgba(0, 0, 0, 0.20)" : "0 8px 24px rgba(15, 52, 66, 0.07)"
  };

  const inputStyle = {
    padding: 12,
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    background: colors.input,
    color: colors.text
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", marginBottom: 22 }}>
        <div>
          <h2 style={{ margin: 0, color: colors.heading }}>İzin Yönetimi</h2>
          <div style={{ marginTop: 6, color: colors.muted, fontWeight: 700 }}>
            İzin talepleri, onay süreci ve yıllık izin bakiyeleri
          </div>
        </div>

        <select
          value={yearFilter}
          onChange={(event) => setYearFilter(Number(event.target.value))}
          style={{ ...inputStyle, padding: "10px 13px", fontWeight: 800 }}
        >
          {[currentYear - 1, currentYear, currentYear + 1].map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {(!isAdmin || adminTab === "mine") && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14, marginBottom: 20 }}>
            {[
              ["Hak Edilen", ownSummary.entitlement, isDark ? "#203449" : "#e0f2fe"],
              ["Kullanılan", ownSummary.used, isDark ? "#352612" : "#fef3c7"],
              ["Kalan", ownSummary.remaining, isDark ? "#123423" : "#dcfce7"]
            ].map(([label, value, background]) => (
              <div key={label} style={{ ...cardStyle, background, borderColor: isDark ? "#334155" : "transparent" }}>
                <div style={{ color: colors.muted, fontSize: 13, fontWeight: 800 }}>{label}</div>
                <div style={{ color: colors.text, fontSize: 28, fontWeight: 900, marginTop: 6 }}>{value} gün</div>
              </div>
            ))}
          </div>

          <div style={{ ...cardStyle, marginBottom: 20 }}>
            <h3 style={{ marginTop: 0, color: colors.heading }}>Yeni İzin Talebi</h3>
            {!user?.hireDate && (
              <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 10, background: colors.warningBg, border: `1px solid ${colors.warningBorder}`, color: colors.warningText, fontWeight: 800, fontSize: 13 }}>
                İşe giriş tarihiniz tanımlı değil. Yıllık izin hesabı için admin tarafından kullanıcı kaydınıza eklenmelidir.
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: 12 }}>
              <select
                value={form.leaveType}
                onChange={(event) => setForm({ ...form, leaveType: event.target.value })}
                style={inputStyle}
              >
                {LEAVE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              <input type="date" value={form.startDate} onChange={(event) => setForm({ ...form, startDate: event.target.value })} style={inputStyle} />
              <input type="date" min={form.startDate} value={form.endDate} onChange={(event) => setForm({ ...form, endDate: event.target.value })} style={inputStyle} />
            </div>
            <textarea
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              placeholder="İzin açıklaması"
              rows={3}
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginTop: 12, resize: "vertical" }}
            />
            <input
              value={form.attachmentUrl}
              onChange={(event) => setForm({ ...form, attachmentUrl: event.target.value })}
              placeholder="Belge bağlantısı (isteğe bağlı)"
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginTop: 12 }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
              <strong style={{ color: colors.heading }}>{requestedDays} iş günü</strong>
              <button onClick={submitRequest} style={{ padding: "11px 18px", border: "none", borderRadius: 10, background: colors.primary, color: "#fff", fontWeight: 900, cursor: "pointer" }}>
                Onaya Gönder
              </button>
            </div>
          </div>
        </>
      )}

      {isAdmin && (
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          <button onClick={() => setAdminTab("requests")} style={{ padding: "10px 15px", border: "none", borderRadius: 10, background: adminTab === "requests" ? colors.primary : colors.soft, color: adminTab === "requests" ? "#fff" : colors.text, fontWeight: 900, cursor: "pointer" }}>
            İzin Talepleri
          </button>
          <button onClick={() => setAdminTab("balances")} style={{ padding: "10px 15px", border: "none", borderRadius: 10, background: adminTab === "balances" ? colors.primary : colors.soft, color: adminTab === "balances" ? "#fff" : colors.text, fontWeight: 900, cursor: "pointer" }}>
            Personel Bakiyeleri
          </button>
          <button onClick={() => setAdminTab("mine")} style={{ padding: "10px 15px", border: "none", borderRadius: 10, background: adminTab === "mine" ? colors.primary : colors.soft, color: adminTab === "mine" ? "#fff" : colors.text, fontWeight: 900, cursor: "pointer" }}>
            İzinlerim
          </button>
        </div>
      )}

      {(!isAdmin || adminTab === "requests" || adminTab === "mine") && (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, color: colors.heading }}>
            {isAdmin && adminTab === "requests" ? "Tüm İzin Talepleri" : "İzin Geçmişim"}
          </h3>
          {loading ? (
            <div style={{ color: colors.muted, fontWeight: 700 }}>Yükleniyor...</div>
          ) : displayedRequests.length === 0 ? (
            <div style={{ color: colors.muted, fontWeight: 700 }}>Bu dönem için izin talebi bulunmuyor.</div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {displayedRequests.map((item) => {
                const type = LEAVE_TYPES.find((entry) => entry.value === item.leaveType);
                const isAdminReview = isAdmin && adminTab === "requests";
                return (
                  <div key={item.id} style={{ border: `1px solid ${colors.cardBorder}`, background: colors.soft, color: colors.text, borderRadius: 14, padding: 14, display: "grid", gridTemplateColumns: isAdminReview ? "1.2fr 1fr 1fr 1.4fr" : "1fr 1fr 1.4fr", gap: 12, alignItems: "center" }}>
                    {isAdminReview && <strong style={{ color: colors.heading }}>{item.employeeName}</strong>}
                    <div>
                      <strong>{type?.label || item.leaveType}</strong>
                      <div style={{ color: colors.muted, fontSize: 12, marginTop: 4 }}>{item.totalDays} iş günü</div>
                    </div>
                    <div style={{ color: colors.muted, fontWeight: 700 }}>{item.startDate} → {item.endDate}</div>
                    <div>
                      <span style={{ ...statusBadgeStyles[item.status], display: "inline-block", padding: "5px 9px", borderRadius: 999, fontSize: 12, fontWeight: 900 }}>
                        {statusLabels[item.status] || item.status}
                      </span>
                      {item.description && <div style={{ marginTop: 6, color: colors.muted, fontSize: 12 }}>{item.description}</div>}
                    </div>
                    {isAdminReview && item.status === "pending" && (
                      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
                        <input
                          value={reviewNotes[item.id] || ""}
                          onChange={(event) => setReviewNotes({ ...reviewNotes, [item.id]: event.target.value })}
                          placeholder="Onay / red notu"
                          style={{ ...inputStyle, flex: 1, padding: 9, borderRadius: 9 }}
                        />
                        <button onClick={() => reviewRequest(item, "approved")} style={{ border: "none", borderRadius: 9, padding: "9px 13px", background: "#16a34a", color: "#fff", fontWeight: 900, cursor: "pointer" }}>Onayla</button>
                        <button onClick={() => reviewRequest(item, "rejected")} style={{ border: "none", borderRadius: 9, padding: "9px 13px", background: "#dc2626", color: "#fff", fontWeight: 900, cursor: "pointer" }}>Reddet</button>
                      </div>
                    )}
                    {(!isAdminReview && item.employeeId === userId) && item.status === "pending" && (
                      <button
                        onClick={async () => {
                          if (!(await window.appConfirm("Bekleyen izin talebi iptal edilsin mi?", { title: "Talep iptal edilsin mi?", type: "warning", confirmText: "İptal et" }))) return;
                          await cancelLeaveRequest(item.id);
                          await loadData();
                        }}
                        style={{ gridColumn: "1 / -1", justifySelf: "end", border: "none", background: colors.soft, color: colors.text, padding: "7px 10px", borderRadius: 8, fontWeight: 800, cursor: "pointer" }}
                      >
                        Talebi İptal Et
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isAdmin && adminTab === "balances" && (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, color: colors.heading }}>{yearFilter} Otomatik Personel İzin Bakiyeleri</h3>
          <div style={{ color: colors.muted, fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
            Her tamamlanan hizmet yılı için otomatik 14 iş günü hak edilir.
          </div>
          <div style={{ display: "grid", gap: 9 }}>
            {users.filter((item) => item.isActive !== false).map((employee) => {
              const employeeId = employee.uid || employee.id;
              const summary = getBalanceSummary(employee, yearFilter);
              return (
                <div key={employeeId} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr .8fr .8fr .8fr", gap: 10, alignItems: "center", border: `1px solid ${colors.cardBorder}`, background: colors.soft, color: colors.text, borderRadius: 12, padding: 12 }}>
                  <div><strong>{employee.name || employee.email}</strong><div style={{ color: colors.muted, fontSize: 12 }}>{employee.email}</div></div>
                  <div><small style={{ color: colors.muted }}>İşe giriş</small><strong style={{ display: "block", color: employee.hireDate ? colors.text : "#f87171" }}>{employee.hireDate || "Tanımsız"}</strong></div>
                  <div><small style={{ color: colors.muted }}>Hak edilen</small><strong style={{ display: "block" }}>{summary.entitlement}</strong></div>
                  <div><small style={{ color: colors.muted }}>Kullanılan</small><strong style={{ display: "block" }}>{summary.used}</strong></div>
                  <div><small style={{ color: colors.muted }}>Kalan</small><strong style={{ display: "block", color: summary.remaining < 0 ? "#f87171" : colors.successText || "#166534" }}>{summary.remaining}</strong></div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default LeaveManagementView;
